import { alpha, Box, Typography, useTheme } from "@mui/material"

type ConnectorBadgeProps = {
  connectorCode: string
  displayName: string
  size?: number
  muted?: boolean
}

const TONES = ["success", "info", "warning", "purple"] as const
type Tone = (typeof TONES)[number]

/** Stable per connector, so a card keeps the same colour between renders. */
const toneFor = (connectorCode: string): Tone => {
  const sum = [...connectorCode].reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return TONES[sum % TONES.length]
}

/** "Foodpanda" -> FO, "TCS Courier" -> TC. */
const initialsFor = (displayName: string): string => {
  const words = displayName.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

/**
 * Stand-in for a provider logo — we hold no brand assets, and a coloured
 * monogram still gives each card something to recognise at a glance.
 * Follows the same alpha-tinted treatment as the picker avatars.
 */
const ConnectorBadge = ({
  connectorCode,
  displayName,
  size = 40,
  muted = false
}: ConnectorBadgeProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === "dark"
  const tone = toneFor(connectorCode)

  const palette: Record<Tone, { bg: string; color: string }> = {
    success: {
      bg: alpha(theme.palette.success.main, isDark ? 0.22 : 0.16),
      color: isDark ? theme.palette.success.light : theme.palette.success.dark
    },
    info: {
      bg: alpha(theme.palette.info.main, isDark ? 0.22 : 0.16),
      color: isDark ? theme.palette.info.light : theme.palette.info.dark
    },
    warning: {
      bg: alpha(theme.palette.warning.main, isDark ? 0.22 : 0.16),
      color: isDark ? theme.palette.warning.light : theme.palette.warning.dark
    },
    purple: {
      bg: isDark ? alpha("#8b6bd0", 0.22) : "#eae3f7",
      color: isDark ? "#c4b5fd" : "#6a4cb0"
    }
  }

  const { bg, color } = palette[tone]

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: bg,
        color,
        opacity: muted ? 0.5 : 1,
        transition: "opacity 0.15s ease"
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: size * 0.34,
          fontWeight: 700,
          letterSpacing: "-0.2px",
          lineHeight: 1
        }}
      >
        {initialsFor(displayName)}
      </Typography>
    </Box>
  )
}

export default ConnectorBadge
